import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Title, Text, Card, Button } from '@tremor/react'
import { DocumentTextIcon, PlusIcon, TrashIcon, PencilIcon, PlayIcon, EyeIcon, LinkIcon, PowerIcon } from '@heroicons/react/24/outline'
import UpgradedLoader from '../UpgradedLoader'
import ConfirmationModal from '../ConfirmationModal'
import ImprovedQuestionnaireBuilderModal from './ImprovedQuestionnaireBuilderModal'
import QuestionnaireInitialSetupModal from './QuestionnaireInitialSetupModal'
import { getQuestionnaires, createQuestionnaire, updateQuestionnaire, deleteQuestionnaire, updateQuestion, getQuestionnaire } from '../../Requests/QuestionnaireRequests'

function QuestionnaireTab({ projectUuid }) {
    const navigate = useNavigate()
    const { t } = useTranslation()
    const [questionnaires, setQuestionnaires] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [showInitialSetupModal, setShowInitialSetupModal] = useState(false)
    const [showCreateModal, setShowCreateModal] = useState(false)
    const [editingQuestionnaire, setEditingQuestionnaire] = useState(null)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [questionnaireToDelete, setQuestionnaireToDelete] = useState(null)
    const [copiedId, setCopiedId] = useState(null)

    const loadQuestionnaires = useCallback(async () => {
        try {
            setLoading(true)
            setError(null)
            const response = await getQuestionnaires(projectUuid)
            setQuestionnaires(response.data || [])
        } catch (err) {
            console.error('Failed to load questionnaires:', err)
            setError(t('Questionnaires.Tab.ErrorLoad'))
        } finally {
            setLoading(false)
        }
    }, [projectUuid])

    // Function to refresh a specific questionnaire in the list with fresh data
    const refreshQuestionnaireInList = useCallback(async (questionnaireUuid) => {
        try {
            const response = await getQuestionnaire(projectUuid, questionnaireUuid);
            const updatedQuestionnaire = response?.questionnaire || null;
            
            if (updatedQuestionnaire) {
                setQuestionnaires(prev => 
                    prev.map(q => q.uuid === questionnaireUuid ? updatedQuestionnaire : q)
                );
            }
        } catch (error) {
            console.error('Failed to refresh questionnaire in list:', error);
        }
    }, [projectUuid])

    useEffect(() => {
        loadQuestionnaires()
    }, [loadQuestionnaires])

    const handleCreateClick = () => {
        setShowInitialSetupModal(true)
    }

    const handleInitialSetupContinue = async (questionnaireData) => {
        try {
            setShowInitialSetupModal(false)
            
            // Create the questionnaire immediately in the database
            console.log('Creating questionnaire with initial data:', questionnaireData)
            const response = await createQuestionnaire(projectUuid, questionnaireData)
            console.log('Questionnaire creation response:', response)
            
            const createdQuestionnaire = response.questionnaire || response.data
            
            // Add to the questionnaires list
            setQuestionnaires(prev => [createdQuestionnaire, ...prev])
            
            // Open the builder modal in "editing" mode with the created questionnaire
            setEditingQuestionnaire(createdQuestionnaire)
            setShowCreateModal(true)
            
            console.log('Questionnaire created successfully, opening builder for editing')
            
        } catch (err) {
            console.error('Failed to create questionnaire from initial setup:', err)
            
            // Show error and reopen the initial setup modal
            alert(err.response?.data?.message || t('Questionnaires.Tab.ErrorLoad'))
            setShowInitialSetupModal(true)
        }
    }

    const handleEditClick = async (questionnaire) => {
        try {
            // Fetch fresh questionnaire data from backend to ensure we have latest questions
            const response = await getQuestionnaire(projectUuid, questionnaire.uuid);
            
            // Extract questionnaire data from response object
            const freshQuestionnaireData = response?.questionnaire || questionnaire;
            
            console.log('📝 Edit click - fetched data:', {
                hasResponse: !!response,
                hasQuestionnaire: !!response?.questionnaire,
                title: freshQuestionnaireData?.title,
                questionsCount: freshQuestionnaireData?.questions?.length
            });
            
            setEditingQuestionnaire(freshQuestionnaireData);
            setShowCreateModal(true);
        } catch (error) {
            console.error('Failed to fetch fresh questionnaire data:', error);
            // Fallback to existing data if fetch fails
            setEditingQuestionnaire(questionnaire);
            setShowCreateModal(true);
        }
    }


    const handleEditSubmit = async (questionnaireData) => {
        try {
            console.log('🔍 Manual save - Received questionnaire data:', {
                title: questionnaireData.title,
                questionsReceived: questionnaireData.questions?.length || 0,
                questionTitles: (questionnaireData.questions || []).map(q => q.question_text || 'EMPTY')
            });
            
            // Update questionnaire metadata
            const response = await updateQuestionnaire(projectUuid, editingQuestionnaire.uuid, questionnaireData)
            
            // Update all questions with their current data including type changes
            if (questionnaireData.questions && questionnaireData.questions.length > 0) {
                const questionUpdatePromises = questionnaireData.questions.map(async (question) => {
                    if (question.uuid) {
                        // Map frontend question types to backend accepted types if needed
                        let backendQuestionType = question.type;
                        if (question.type === 'image_only') {
                            backendQuestionType = 'image_choice';
                        } else if (question.type === 'video_only') {
                            backendQuestionType = 'image_choice';
                        }

                        const questionUpdateData = {
                            question_text: question.question_text,
                            type: backendQuestionType,
                            required: question.required || false,
                            options: question.options || [],
                            order: question.order_index || question.order || 0
                        };

                        console.log('🔍 Updating individual question:', {
                            uuid: question.uuid,
                            question_text: question.question_text,
                            hasText: !!question.question_text,
                            type: question.type,
                            optionsCount: (question.options || []).length
                        });


                        // Include timing fields if they have valid values
                        if (question.view_duration !== null && question.view_duration !== undefined) {
                            questionUpdateData.view_duration = question.view_duration;
                        }
                        if (question.answer_duration !== null && question.answer_duration !== undefined) {
                            questionUpdateData.answer_duration = question.answer_duration;
                        }

                        return updateQuestion(projectUuid, editingQuestionnaire.uuid, question.uuid, questionUpdateData);
                    }
                    return null;
                }).filter(Boolean);

                await Promise.all(questionUpdatePromises);
            }
            
            setQuestionnaires(prev => 
                prev.map(q => q.uuid === editingQuestionnaire.uuid ? (response.questionnaire || response.data) : q)
            )
            setShowCreateModal(false)
            setEditingQuestionnaire(null)
        } catch (err) {
            console.error('Failed to update questionnaire:', err)
            throw new Error(err.response?.data?.message || 'Failed to update questionnaire')
        }
    }

    const handleDeleteClick = (questionnaireUuid) => {
        setQuestionnaireToDelete(questionnaireUuid)
        setShowDeleteModal(true)
    }

    const handleDeleteConfirm = async () => {
        if (!questionnaireToDelete) return

        try {
            await deleteQuestionnaire(projectUuid, questionnaireToDelete)
            setQuestionnaires(prev => prev.filter(q => q.uuid !== questionnaireToDelete))
            setShowDeleteModal(false)
            setQuestionnaireToDelete(null)
        } catch (err) {
            console.error('Failed to delete questionnaire:', err)
            alert(t('Questionnaires.Tab.ErrorDelete'))
        }
    }

    const handleCopyLink = async (questionnaireUuid) => {
        try {
            const link = `${window.location.origin}/questionnaire/${questionnaireUuid}`
            await navigator.clipboard.writeText(link)
            
            // Show feedback
            setCopiedId(questionnaireUuid)
            setTimeout(() => setCopiedId(null), 2000)
        } catch (err) {
            console.error('Failed to copy link:', err)
            // Fallback for older browsers
            try {
                const textArea = document.createElement('textarea')
                textArea.value = `${window.location.origin}/questionnaire/${questionnaireUuid}`
                document.body.appendChild(textArea)
                textArea.select()
                document.execCommand('copy')
                document.body.removeChild(textArea)
                
                setCopiedId(questionnaireUuid)
                setTimeout(() => setCopiedId(null), 2000)
            } catch (fallbackErr) {
                alert(t('Questionnaires.Tab.ErrorCopyLink'))
            }
        }
    }

    const handleToggleStatus = async (questionnaire) => {
        try {
            const newStatus = questionnaire.status === 'active' ? 'draft' : 'active'
            const response = await updateQuestionnaire(projectUuid, questionnaire.uuid, {
                ...questionnaire,
                status: newStatus
            })
            
            setQuestionnaires(prev => 
                prev.map(q => q.uuid === questionnaire.uuid ? (response.questionnaire || response.data) : q)
            )
        } catch (err) {
            console.error('Failed to update questionnaire status:', err)
            alert(t('Questionnaires.Tab.ErrorUpdateStatus'))
        }
    }

    const getStatusBadge = (status) => {
        const statusColors = {
            draft: 'bg-gray-100 text-gray-800',
            active: 'bg-green-100 text-green-800',
            paused: 'bg-yellow-100 text-yellow-800',
            closed: 'bg-red-100 text-red-800'
        }
        const statusLabels = {
            draft: t('Questionnaires.Tab.StatusDraft'),
            active: t('Questionnaires.Tab.StatusActive'),
            paused: t('Questionnaires.Tab.StatusPaused'),
            closed: t('Questionnaires.Tab.StatusClosed'),
        }
        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[status] || statusColors.draft}`}>
                {statusLabels[status] || (status.charAt(0).toUpperCase() + status.slice(1))}
            </span>
        )
    }

    if (loading) {
        return (
            <div className="flex justify-center items-center py-12">
                <UpgradedLoader className="animate-spin" w={8} h={8} bg="bg-iavisus-100" />
            </div>
        )
    }

    return (
        <div data-testid="questionnaire-tab-container" className="space-y-6">
            <div>
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                        <DocumentTextIcon className="h-6 w-6 text-iavisus-100" />
                        <Title className="text-lg font-semibold">
                            {t('Questionnaires.Tab.Title')}
                        </Title>
                    </div>
                    <div className="flex space-x-2">
                        <Button
                            data-testid="questionnaire-tab-create-btn"
                            size="sm"
                            icon={PlusIcon}
                            onClick={handleCreateClick}
                            className="bg-iavisus-100 text-white hover:bg-iavisus-200"
                        >
                            {t('Questionnaires.Tab.CreateButton')}
                        </Button>
                    </div>
                </div>
                
                <Text className="text-gray-600 mb-6">
                    {t('Questionnaires.Tab.Description')}
                </Text>

                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                        <Text className="text-red-600">{error}</Text>
                    </div>
                )}

                {questionnaires.length === 0 ? (
                    <div data-testid="questionnaire-tab-empty" className="bg-gray-50 rounded-lg p-6 text-center">
                        <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <Text className="text-gray-500 mb-4">
                            {t('Questionnaires.Tab.EmptyTitle')}
                        </Text>
                        <Text className="text-gray-400 text-sm">
                            {t('Questionnaires.Tab.EmptyDescription')}
                        </Text>
                        <Button
                            data-testid="questionnaire-tab-empty-create-btn"
                            size="sm"
                            icon={PlusIcon}
                            onClick={handleCreateClick}
                            className="mt-4 bg-iavisus-100 text-white hover:bg-iavisus-200"
                        >
                            {t('Questionnaires.Tab.CreateFirstButton')}
                        </Button>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {questionnaires.map((questionnaire) => (
                            <Card key={questionnaire.uuid} data-testid={`questionnaire-tab-item-${questionnaire.uuid}`} className="p-4">
                                <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                        <div className="flex items-center space-x-2 mb-2">
                                            {getStatusBadge(questionnaire.status)}
                                            <span className="text-xs text-gray-500">
                                                {questionnaire.questions_count} {questionnaire.questions_count !== 1 ? t('Questionnaires.Tab.QuestionPlural') : t('Questionnaires.Tab.QuestionSingular')}
                                            </span>
                                        </div>
                                        <Text className="text-gray-900 mb-2 font-medium">
                                            {questionnaire.title || t('Questionnaires.Tab.UntitledQuestionnaire')}
                                        </Text>
                                        {questionnaire.description && (
                                            <Text className="text-sm text-gray-600 mb-2">
                                                {questionnaire.description}
                                            </Text>
                                        )}
                                        <div className="flex items-center space-x-4 text-xs text-gray-500">
                                            <span>{t('Questionnaires.Tab.CreatedBy', { name: questionnaire.creator?.name || 'Unknown' })}</span>
                                            <span>{new Date(questionnaire.created_at).toLocaleDateString()}</span>
                                            <span>{questionnaire.total_responses} {questionnaire.total_responses !== 1 ? t('Questionnaires.Tab.ResponsePlural') : t('Questionnaires.Tab.ResponseSingular')}</span>
                                            {questionnaire.completion_rate > 0 && (
                                                <span>{t('Questionnaires.Tab.CompletionRate', { rate: questionnaire.completion_rate })}</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex space-x-2 ml-4">
                                        <button
                                            onClick={() => console.log('View responses:', questionnaire.uuid)}
                                            className="p-1 text-gray-500 hover:text-gray-700"
                                            title={t('Questionnaires.Tab.TooltipViewResponses')}
                                        >
                                            <EyeIcon className="h-4 w-4" />
                                        </button>
                                        {questionnaire.status === 'active' && (
                                            <>
                                                <button
                                                    onClick={() => navigate(`/questionnaire/${questionnaire.uuid}`)}
                                                    className="p-1 text-iavisus-100 hover:text-iavisus-200"
                                                    title={t('Questionnaires.Tab.TooltipPreview')}
                                                >
                                                    <PlayIcon className="h-4 w-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleCopyLink(questionnaire.uuid)}
                                                    className={`p-1 transition-colors ${
                                                        copiedId === questionnaire.uuid 
                                                            ? 'text-green-600 hover:text-green-700' 
                                                            : 'text-blue-500 hover:text-blue-700'
                                                    }`}
                                                    title={copiedId === questionnaire.uuid ? t('Questionnaires.Tab.TooltipLinkCopied') : t('Questionnaires.Tab.TooltipCopyLink')}
                                                >
                                                    <LinkIcon className="h-4 w-4" />
                                                </button>
                                            </>
                                        )}
                                        <button
                                            onClick={() => handleToggleStatus(questionnaire)}
                                            className={`p-1 transition-colors ${
                                                questionnaire.status === 'active' 
                                                    ? 'text-orange-500 hover:text-orange-700' 
                                                    : 'text-green-500 hover:text-green-700'
                                            }`}
                                            title={questionnaire.status === 'active' ? t('Questionnaires.Tab.TooltipDeactivate') : t('Questionnaires.Tab.TooltipActivate')}
                                        >
                                            <PowerIcon className="h-4 w-4" />
                                        </button>
                                        <button
                                            onClick={() => handleEditClick(questionnaire)}
                                            className="p-1 text-gray-500 hover:text-gray-700"
                                            title={t('Questionnaires.Tab.TooltipEdit')}
                                        >
                                            <PencilIcon className="h-4 w-4" />
                                        </button>
                                        <button
                                            onClick={() => handleDeleteClick(questionnaire.uuid)}
                                            className="p-1 text-red-500 hover:text-red-700"
                                            title={t('Questionnaires.Tab.TooltipDelete')}
                                        >
                                            <TrashIcon className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </div>
                )}
            </div>

            {/* Delete Confirmation Modal */}
            <ConfirmationModal
                open={showDeleteModal}
                setOpen={setShowDeleteModal}
                title={t('Questionnaires.Tab.DeleteTitle')}
                message={t('Questionnaires.Tab.DeleteMessage')}
                confirmText={t('Questionnaires.Tab.DeleteConfirm')}
                cancelText={t('Questionnaires.Tab.Cancel')}
                variant="danger"
                onConfirm={handleDeleteConfirm}
            />

            {/* Initial Setup Modal */}
            <QuestionnaireInitialSetupModal
                isOpen={showInitialSetupModal}
                onClose={() => {
                    setShowInitialSetupModal(false)
                }}
                onContinue={handleInitialSetupContinue}
            />

            {/* Create/Edit Modal */}
            <ImprovedQuestionnaireBuilderModal
                isOpen={showCreateModal}
                onClose={() => {
                    // Refresh the questionnaire list when modal closes to sync any changes
                    if (editingQuestionnaire?.uuid) {
                        refreshQuestionnaireInList(editingQuestionnaire.uuid);
                    }
                    setShowCreateModal(false)
                    setEditingQuestionnaire(null)
                }}
                onSubmit={handleEditSubmit}
                initialData={editingQuestionnaire}
                isEditing={true}
                projectUuid={projectUuid}
                onQuestionUpdate={(updatedData) => {
                    // Only update the questionnaire list for manual saves, not auto-saves
                    // Auto-save should not refresh the frontend data while user is editing
                    console.log('Question update received, but not refreshing during editing');
                }}
            />
        </div>
    )
}

export default QuestionnaireTab